import gql from 'graphql-tag';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';

import { getLogger } from '@confluence/logger';
import { usePageSpaceKey } from '@confluence/page-context';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';
import { getLegacyMessageQueue } from '@confluence/legacy-message-queue';

import { PseudoCreateDialogComponent } from './PseudoCreateDialogComponent.js';
import { navigateToNewContent } from './navigateToNewContent.js';

/**
 * Wrapper for PseudoCreateDialog. We can query for the (likely) cached page
 * title, and pass it to the dialog. The dialog displays what the parent page
 * will be to the user.
 */
const PseudoCreateDialog = (props) => {
	const logger = getLogger('PseudoCreateDialog');
	const { parentPageId } = props;

	const { push } = useRouteActions();
	const [spaceKey] = usePageSpaceKey();
	const isNotContextual = !parentPageId;

	const { data, error, loading } = useQuery(
		gql`
			query PseudoCreateDialogQuery($parentPageId: ID!) {
				content(id: $parentPageId) {
					nodes {
						id
						title
						type
						space {
							id
							key
						}
					}
				}
			}
		`,
		{
			variables: { parentPageId },
			skip: isNotContextual,
		},
	);

	useEffect(() => {
		void getLegacyMessageQueue().then((legacyMessageQueue) => {
			legacyMessageQueue.push({
				type: 'ATLASSIAN_CONFLUENCE_CREATE_PAGE_NAVIGATE_CALLBACK',
				payload: navigateToNewContent(push),
			});
		});
	}, [push]);

	if (isNotContextual) {
		return <PseudoCreateDialogComponent spaTransitionEnabled spaceKey={spaceKey} {...props} />;
	}

	if (loading) {
		return null;
	}

	if (error) {
		logger.error`${error}`;
		return null;
	}

	const parentPageType = data?.content?.nodes?.[0]?.type;
	const parentPageTitle = data?.content?.nodes?.[0]?.title;
	const parentPageSpaceKey = data?.content?.nodes?.[0]?.space?.key;

	return (
		<PseudoCreateDialogComponent
			spaTransitionEnabled
			parentPageTitle={parentPageTitle}
			parentPageType={parentPageType}
			parentPageSpaceKey={parentPageSpaceKey}
			spaceKey={spaceKey}
			{...props}
		/>
	);
};

PseudoCreateDialog.propTypes = {
	spaceKey: PropTypes.string,
	shouldSuppressDialog: PropTypes.bool,
	onClose: PropTypes.func,
	open: PropTypes.bool,
	//The ID of the parent page under which to create a page. Leave blank for
	//current page.
	parentPageId: PropTypes.string,
};

export { PseudoCreateDialog };
